import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Play,
  RefreshCw,
  MessageSquare,
  Cake,
  Lightbulb,
  ListFilter,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../services/api';

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtDatetime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(d);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d)) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(d);
};

const STATUS_STYLE = {
  success: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  failed:  'bg-red-500/15 text-red-300 border-red-500/30',
  pending: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  unknown: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};
const STATUS_LABEL = {
  success: 'Sucesso',
  failed:  'Erro',
  pending: 'Agendado',
  unknown: '?',
};

const DAY_CLASS_LABEL = {
  segunda:     'Segunda',
  quarta:      'Quarta',
  sexta:       'Sexta',
  domingo:     'Domingo',
  aniversario: 'Aniversário',
  daily:       'Diário',
};

const JOB_TYPE_LABEL = {
  goal_reminder: 'Lembrete de Meta',
  birthday:      'Aniversário',
  practical_tip: 'Dica Prática',
  welcome:       'Boas-vindas',
};

const JOB_TYPE_ICON = {
  goal_reminder: <MessageSquare size={14} />,
  birthday:      <Cake size={14} />,
  practical_tip: <Lightbulb size={14} />,
  welcome:       <CheckCircle2 size={14} />,
};

// ─── Micro view ─────────────────────────────────────────────────────────────

const MessageRow = ({ row, onRetry }) => {
  const [expanded, setExpanded] = useState(false);
  const statusStyle = STATUS_STYLE[row.status] || STATUS_STYLE.unknown;

  return (
    <>
      <div
        className="grid grid-cols-12 gap-2 items-center px-4 py-3 rounded-lg border border-white/5 bg-white/2 hover:bg-white/5 cursor-pointer transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="col-span-1 flex items-center justify-center">
          {JOB_TYPE_ICON[row.job_type] || <MessageSquare size={14} />}
        </div>
        <div className="col-span-2">
          <span className="text-xs text-slate-400">{JOB_TYPE_LABEL[row.job_type] || row.job_type}</span>
          <div className="text-xs text-slate-500">{DAY_CLASS_LABEL[row.day_class] || row.day_class}</div>
        </div>
        <div className="col-span-2 text-xs text-slate-400">{fmtDatetime(row.timestamp)}</div>
        <div className="col-span-3 text-sm text-white truncate">{row.user_name}</div>
        <div className="col-span-2">
          <Badge className={`text-xs border ${statusStyle}`}>
            {STATUS_LABEL[row.status] || row.status}
          </Badge>
        </div>
        <div className="col-span-2 flex items-center justify-end gap-2">
          {row.can_retry && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-500/30 text-amber-300 hover:bg-amber-500/10"
              onClick={(e) => { e.stopPropagation(); onRetry(row); }}
            >
              <RefreshCw size={12} className="mr-1" />
              Retry
            </Button>
          )}
        </div>
      </div>
      {expanded && (
        <div className="mx-2 mb-2 px-4 py-3 rounded-b-lg bg-slate-900/60 border border-t-0 border-white/5 text-xs space-y-2">
          {row.message_preview && (
            <div>
              <span className="text-slate-500 font-medium">Mensagem enviada:</span>
              <pre className="mt-1 text-slate-300 whitespace-pre-wrap font-sans">{row.message_preview}</pre>
            </div>
          )}
          {row.error && (
            <div>
              <span className="text-red-400 font-medium">Erro:</span>
              <pre className="mt-1 text-red-300/80 whitespace-pre-wrap font-mono text-[11px]">{row.error}</pre>
            </div>
          )}
          <div className="text-slate-600 font-mono">ID: {row.id}</div>
        </div>
      )}
    </>
  );
};

const MicroView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobType, setJobType] = useState('all');
  const [status, setStatus] = useState('all');
  const [limit, setLimit] = useState(50);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (jobType !== 'all') params.job_type = jobType;
      if (status !== 'all') params.status = status;
      const res = await api.get('/admin/jobs/messages', { params });
      setItems(res.data?.items || []);
    } catch {
      toast.error('Não foi possível carregar os envios.');
    } finally {
      setLoading(false);
    }
  }, [jobType, status, limit]);

  useEffect(() => { load(); }, [load]);

  const handleRetry = async (row) => {
    toast.info(`Retry não disponível ainda para ${row.job_type}.`);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={jobType} onValueChange={setJobType}>
          <SelectTrigger className="w-44 h-9 text-sm bg-secondary border-white/10">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="goal_reminder">Lembrete de Meta</SelectItem>
            <SelectItem value="birthday">Aniversário</SelectItem>
            <SelectItem value="practical_tip">Dica Prática</SelectItem>
            <SelectItem value="welcome">Boas-vindas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9 text-sm bg-secondary border-white/10">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos status</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="failed">Erro</SelectItem>
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
          <SelectTrigger className="w-28 h-9 text-sm bg-secondary border-white/10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">25 linhas</SelectItem>
            <SelectItem value="50">50 linhas</SelectItem>
            <SelectItem value="100">100 linhas</SelectItem>
            <SelectItem value="200">200 linhas</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={load} className="text-slate-400 hover:text-white">
          <RefreshCw size={14} className="mr-1" />
          Atualizar
        </Button>
        <span className="ml-auto text-xs text-slate-500">{items.length} registros</span>
      </div>

      {/* Header row */}
      <div className="grid grid-cols-12 gap-2 px-4 py-1 text-xs text-slate-500 uppercase tracking-wider">
        <div className="col-span-1" />
        <div className="col-span-2">Tipo / Classe</div>
        <div className="col-span-2">Data/Hora</div>
        <div className="col-span-3">Usuário</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-2 text-right">Ações</div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full bg-white/5 rounded-lg" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <MessageSquare size={32} className="mx-auto mb-3 opacity-40" />
          <p>Nenhum envio encontrado com esses filtros.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {items.map((row) => (
            <MessageRow key={row.id} row={row} onRetry={handleRetry} />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Macro view ──────────────────────────────────────────────────────────────

const WeekCell = ({ status }) => {
  if (!status) return <span className="text-slate-600 text-sm">—</span>;
  const style = STATUS_STYLE[status] || STATUS_STYLE.unknown;
  const label = STATUS_LABEL[status] || status;
  return <Badge className={`text-xs border ${style}`}>{label}</Badge>;
};

const MacroView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/jobs/weekly-summary', { params: { week_offset: weekOffset } });
      setData(res.data);
    } catch {
      toast.error('Não foi possível carregar o resumo semanal.');
    } finally {
      setLoading(false);
    }
  }, [weekOffset]);

  useEffect(() => { load(); }, [load]);

  const weekLabel = data
    ? `${fmtDate(data.week_start)} → ${fmtDate(data.week_end)}`
    : '—';

  return (
    <div className="space-y-4">
      {/* Week navigation */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset((w) => w + 1)}
          className="text-slate-400 hover:text-white"
        >
          <ChevronLeft size={16} />
          Semana anterior
        </Button>
        <span className="text-sm text-white font-medium px-3 py-1 rounded bg-white/5">{weekLabel}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setWeekOffset((w) => Math.max(0, w - 1))}
          disabled={weekOffset === 0}
          className="text-slate-400 hover:text-white disabled:opacity-30"
        >
          Semana seguinte
          <ChevronRight size={16} />
        </Button>
        {weekOffset > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} className="text-blue-400 hover:text-blue-300">
            Voltar à semana atual
          </Button>
        )}
      </div>

      {/* Table header */}
      <div className="grid grid-cols-12 gap-2 px-4 py-1 text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
        <div className="col-span-4">Usuário</div>
        <div className="col-span-2 text-center">Segunda</div>
        <div className="col-span-2 text-center">Quarta</div>
        <div className="col-span-2 text-center">Sexta</div>
        <div className="col-span-2 text-center">Domingo</div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full bg-white/5 rounded-lg" />
          ))}
        </div>
      ) : !data || data.users.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Calendar size={32} className="mx-auto mb-3 opacity-40" />
          <p>Nenhum dado para esta semana.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {data.users.map((row) => (
            <div
              key={row.user_id}
              className="grid grid-cols-12 gap-2 items-center px-4 py-3 rounded-lg border border-white/5 bg-white/2 hover:bg-white/5 transition-colors"
            >
              <div className="col-span-4">
                <span className="text-sm text-white">{row.user_name}</span>
              </div>
              <div className="col-span-2 flex justify-center"><WeekCell status={row.segunda} /></div>
              <div className="col-span-2 flex justify-center"><WeekCell status={row.quarta} /></div>
              <div className="col-span-2 flex justify-center"><WeekCell status={row.sexta} /></div>
              <div className="col-span-2 flex justify-center"><WeekCell status={row.domingo} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Scheduler status ────────────────────────────────────────────────────────

const SchedulerStatus = ({ onTrigger }) => {
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [triggeringId, setTriggeringId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/jobs/scheduler-status');
      setJobs(res.data?.jobs || []);
      setRunning(res.data?.running || false);
    } catch {
      toast.error('Não foi possível carregar o status do scheduler.');
    } finally {
      setLoading(false);
    }
  };

  const handleTrigger = async (jobId) => {
    const confirmMap = {
      birthday: 'Disparar verificação de aniversários agora?',
    };
    if (!window.confirm(confirmMap[jobId] || `Disparar ${jobId}?`)) return;
    setTriggeringId(jobId);
    try {
      await api.post(`/admin/jobs/trigger/${jobId}`, null, { params: { force: true } });
      toast.success(`Job '${jobId}' disparado em background.`);
      onTrigger?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao disparar job.');
    } finally {
      setTriggeringId(null);
    }
  };

  const TRIGGER_JOBS = [
    { id: 'birthday', label: 'Aniversários' },
  ];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="border-white/10 text-slate-300 hover:text-white"
        onClick={() => { setOpen(true); load(); }}
      >
        <Activity size={14} className="mr-2" />
        Scheduler
        <span className={`ml-2 h-2 w-2 rounded-full ${running ? 'bg-emerald-400' : 'bg-red-400'}`} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Activity size={18} />
              Status do Scheduler
              <Badge className={running ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-red-500/15 text-red-300 border-red-500/30'}>
                {running ? 'Ativo' : 'Inativo'}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8 bg-white/5" />)}</div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Jobs Agendados</p>
                {jobs.map((job) => (
                  <div key={job.id} className="flex items-start justify-between px-3 py-2 rounded bg-white/5 text-sm">
                    <div>
                      <p className="text-white font-medium">{job.id}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        <Clock size={10} className="inline mr-1" />
                        Próximo: {job.next_run ? fmtDatetime(job.next_run) : '—'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Disparar manualmente</p>
                <div className="flex flex-wrap gap-2">
                  {TRIGGER_JOBS.map((tj) => (
                    <Button
                      key={tj.id}
                      size="sm"
                      variant="outline"
                      className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
                      onClick={() => handleTrigger(tj.id)}
                      disabled={triggeringId === tj.id}
                    >
                      {triggeringId === tj.id ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Play size={12} className="mr-1" />}
                      {tj.label}
                    </Button>
                  ))}
                </div>
              </div>

              {!running && (
                <div className="flex items-center gap-2 px-3 py-2 rounded bg-red-500/10 border border-red-500/20 text-sm text-red-300">
                  <AlertTriangle size={14} />
                  <span>Scheduler inativo — jobs não estão sendo executados automaticamente.</span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'micro', label: 'Envios', icon: <ListFilter size={16} /> },
  { id: 'macro', label: 'Semanas', icon: <Calendar size={16} /> },
];

const AdminJobsPage = () => {
  const [activeTab, setActiveTab] = useState('micro');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Gerenciamento de Jobs</h1>
            <p className="text-slate-400 text-sm mt-1">
              Acompanhe cada envio automático do ELIOS no WhatsApp.
            </p>
          </div>
          <SchedulerStatus onTrigger={() => setRefreshKey((k) => k + 1)} />
        </div>

        <Card className="glass-card border-white/5">
          <CardHeader className="pb-0">
            <div className="flex gap-1 border-b border-white/5 pb-0">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'border-blue-400 text-white'
                      : 'border-transparent text-slate-400 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {activeTab === 'micro' && <MicroView key={`micro-${refreshKey}`} />}
            {activeTab === 'macro' && <MacroView key={`macro-${refreshKey}`} />}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminJobsPage;
